import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  FileText,
  RefreshCw,
  ClipboardCheck,
  CalendarCheck,
  Shield,
  HardHat,
  Building2,
  BadgeCheck,
  Headphones,
  ArrowRight,
} from "lucide-react";

const childrenImg = "/assets/images/bg2.jpeg";
const serviceLongImg = "/assets/images/bgb3.png";

const services = [
  {
    id: 1,
    icon: FileText,
    title: "Registration of Free Zone Enterprise",
    description:
      "Registration service for companies seeking to operate within Nigeria’s Oil & Gas Free Zones.",
    link: "/services/registration-free-zone-enterprise",
  },
  {
    id: 2,
    icon: RefreshCw,
    title: "Renewal of Licence",
    description:
      "Licence renewal support for existing free zone enterprises, developers, and operators.",
    link: "/services/renewal-of-licence",
  },
  {
    id: 3,
    icon: ClipboardCheck,
    title: "Application for Licence",
    description:
      "Application processing for enterprise licences, developer licences, service licences, and special activity licences.",
    link: "/services/application-for-licence",
  },
  {
    id: 4,
    icon: CalendarCheck,
    title: "Submission of Annual Returns",
    description:
      "Annual returns submission and compliance documentation for licensed free zone enterprises.",
    link: "/services/submission-of-annual-returns",
  },
  {
    id: 5,
    icon: Shield,
    title: "HSE",
    description:
      "Health, safety, and environmental compliance services for free zone operations.",
    link: "/services/hse",
  },
  {
    id: 6,
    icon: BadgeCheck,
    title: "Service Charter",
    description:
      "Service standards, procedures, requirements, timelines, and regulatory support information.",
    link: "/services/service-charter",
  },
  {
    id: 7,
    icon: HardHat,
    title: "Engineering",
    description:
      "Engineering support for infrastructure coordination, technical operations, and free zone project development.",
    link: "/services/engineering",
  },
  {
    id: 8,
    icon: Building2,
    title: "Registry",
    description:
      "Registry services covering enterprise registration, licensing records, documentation, and renewals.",
    link: "/services/registry",
  },
  {
    id: 9,
    icon: Headphones,
    title: "Operations",
    description:
      "Operational support for licence applications, annual returns, compliance, and investor service coordination.",
    link: "/services/operations",
  },
];

const Services = () => {
  const [highlightId, setHighlightId] = useState(2);

  return (
    <section className="relative py-20 overflow-hidden">
      {/* BG IMAGE */}
      <div
        className="absolute inset-0"
        style={{
          backgroundImage: `url(${childrenImg})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundAttachment: "fixed",
        }}
      />

      {/* OVERLAY */}
      <div className="absolute inset-0 bg-white/85 backdrop-blur-[1px]" />

      {/* GRID PATTERN */}
      <div
        className="absolute inset-0 opacity-[0.04] pointer-events-none"
        style={{
          backgroundImage:
            "radial-gradient(circle at 1px 1px, #06183f 1px, transparent 0)",
          backgroundSize: "24px 24px",
        }}
      />

      {/* GREEN SPLASH */}
      <div className="absolute -left-32 top-0 w-[420px] h-[420px] bg-[#1f9d4c]/12 rounded-full blur-3xl pointer-events-none animate-pulse" />

      {/* YELLOW SPLASH */}
      <div className="absolute -right-32 bottom-0 w-[420px] h-[420px] bg-[#d4a017]/12 rounded-full blur-3xl pointer-events-none animate-pulse" />

      {/* BLUE SPLASH */}
      <div className="absolute top-1/2 right-1/3 w-[350px] h-[350px] bg-[#06183f]/10 rounded-full blur-3xl pointer-events-none animate-pulse" />

      {/* TOP LINE */}
      <div className="absolute top-0 left-0 w-full h-[5px] bg-gradient-to-r from-[#06183f] via-[#1f9d4c] to-[#d4a017]" />

      {/* CONTENT */}
      <div className="relative max-w-[1200px] mx-auto px-6 grid lg:grid-cols-[0.95fr_1.05fr] gap-10 items-start">
        {/* LEFT */}
        <div className="lg:sticky lg:top-24">
          <div className="flex items-center gap-3 mb-5">
            <span className="w-10 h-[3px] bg-[#d4a017]" />

            <span className="text-[#1f9d4c] font-bold tracking-[0.2em] text-sm uppercase">
              Services
            </span>
          </div>

          <h2
            className="text-[42px] md:text-[56px] lg:text-[62px] font-black text-[#06183f] leading-[1.02] mb-7"
            style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
          >
            OGFZA provides <br />
            these <span className="text-[#1f9d4c]">key</span> <br />
            services.
          </h2>

          <p className="text-[#5b6478] leading-[1.9] text-[16px] mb-8 max-w-md">
            OGFZA provides regulatory, operational, licensing, compliance,
            engineering, registry, and investor support services designed to
            facilitate seamless business operations within Nigeria’s Oil & Gas
            Free Zones.
          </p>

          {/* PORTRAIT IMAGE */}
          <div className="relative mb-8 max-w-md overflow-hidden rounded-[32px] shadow-2xl group">
            <img
              src={serviceLongImg}
              alt="OGFZA Services"
              className="w-full h-[500px] object-cover object-top transition-transform duration-700 group-hover:scale-105"
            />

            <div className="absolute inset-0 bg-gradient-to-t from-[#06183f]/80 via-[#06183f]/20 to-transparent" />

            <div className="absolute left-0 bottom-0 w-full p-6">
              <p className="text-white text-[13px] font-bold uppercase tracking-[0.18em] mb-2">
                Investor Support
              </p>

              <h3
                className="text-white text-[28px] font-black leading-tight"
                style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
              >
                Seamless Free Zone Services
              </h3>
            </div>
          </div>

          {/* BUTTON */}
          <Link
            to="/services"
            className="group relative inline-flex items-center gap-3 pl-7 pr-12 py-4 bg-[#06183f] text-white font-bold hover:bg-[#1f9d4c] transition-all duration-300 shadow-xl hover:scale-105"
            style={{
              clipPath:
                "polygon(0 0, 100% 0, 92% 50%, 100% 100%, 0 100%)",
            }}
          >
            Explore Services

            <ArrowRight
              size={18}
              className="absolute right-5 group-hover:translate-x-1 transition-transform"
            />
          </Link>
        </div>

        {/* RIGHT */}
        <div className="grid sm:grid-cols-2 gap-5">
          {services.map((s) => {
            const Icon = s.icon;

            const isHighlighted = s.id === highlightId;

            const cardClass = isHighlighted
              ? "bg-[#06183f] text-white shadow-2xl"
              : "bg-white/95 backdrop-blur-md text-[#06183f] shadow-sm hover:shadow-2xl hover:-translate-y-2 hover:bg-[#06183f] hover:text-white";

            return (
              <Link
                key={s.id}
                to={s.link}
                onMouseEnter={() => setHighlightId(s.id)}
                className={`group relative p-7 pt-12 rounded-[28px] transition-all duration-300 cursor-pointer overflow-hidden border border-white/40 ${cardClass}`}
              >
                {/* TOP BAR */}
                <span
                  className={`absolute top-0 left-0 h-1.5 transition-all duration-500 ${
                    isHighlighted
                      ? "w-1/3 bg-[#d4a017]"
                      : "w-28 bg-[#d4a017] group-hover:w-full"
                  }`}
                />

                {/* BLUE SHAPE */}
                <div className="absolute -right-16 -top-16 w-40 h-40 rounded-full bg-[#06183f]/10 group-hover:bg-[#d4a017]/10 transition-all duration-500" />

                {/* GREEN SHAPE */}
                <div className="absolute -left-20 -bottom-20 w-44 h-44 rounded-full bg-[#1f9d4c]/8 group-hover:bg-[#1f9d4c]/15 transition-all duration-500" />

                {/* ICON */}
                <div
                  className={`relative w-16 h-16 mb-6 flex items-center justify-center rounded-2xl transition-all duration-300 ${
                    isHighlighted
                      ? "bg-white/10 text-[#d4a017]"
                      : "bg-[#06183f]/5 text-[#06183f] group-hover:bg-[#1f9d4c] group-hover:text-white"
                  }`}
                >
                  <Icon size={32} strokeWidth={1.6} />
                </div>

                {/* TITLE */}
                <h3
                  className={`relative text-[20px] font-black mb-3 leading-tight transition-colors ${
                    isHighlighted
                      ? "text-white"
                      : "text-[#06183f] group-hover:text-white"
                  }`}
                  style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
                >
                  {s.title}
                </h3>

                {/* DESCRIPTION */}
                <p
                  className={`relative leading-relaxed text-[14.5px] transition-colors ${
                    isHighlighted
                      ? "text-white/80"
                      : "text-[#5b6478] group-hover:text-white/80"
                  }`}
                >
                  {s.description}
                </p>

                {/* LARGE BG ICON */}
                {isHighlighted && (
                  <Icon
                    size={160}
                    strokeWidth={0.6}
                    className="absolute -bottom-8 -right-6 text-white/5 pointer-events-none"
                  />
                )}
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Services;